import {
  zoomTransform,
  select,
  event,
  format,
  timeFormat,
  line as lineD3,
  axisRight,
} from "d3";
import {
  grayLightBackground,
  textactive,
  textactive2,
} from "@/_core/_helpers/_parametric_tools";
import {
  barCreate,
  dotCreate,
  hLineCreate,
  vLineCreate,
} from "@/_core/charts/D3ChartHoverHelpers";
import {
  createTooltips,
  displayDataTA,
  inRange,
  mouseDate,
  roundDate,
  zoomSetup,
} from "@/_core/charts/TechnicalAnalysis";

export function MACDHover(
  _this,
  upColor,
  dnColor,
  barWidth = 0.7,
  chartBody,
  colorTheme,
) {
  const auxName = "MACD";
  let valueLast = _this.candleData[_this.candleData.length - 1].macd;
  let xTooltipWidth = 80;
  let yTooltip = createTooltips(
    _this.svgNodeMACD,
    _this.currencyFormat,
    valueLast,
    xTooltipWidth,
    _this.colorTheme,
    auxName,
  );
  let lastYTooltip = yTooltip["lastTooltip"];
  _this.svgNodeMACD.lastYTooltip = lastYTooltip;
  let xTooltip = yTooltip["xTooltip"];
  yTooltip = yTooltip["yTooltip"];
  hLineCreate(
    _this.svgNodeMACD,
    auxName + _this.chartName,
    grayLightBackground(colorTheme),
    1,
  );
  vLineCreate(
    _this.svgNodeMACD,
    auxName + _this.chartName,
    grayLightBackground(colorTheme),
    1,
  );
  barCreate(
    _this.svgNodeMACD,
    "MACDHistogram" + _this.chartName,
    textactive(colorTheme),
    3.5,
    chartBody,
  );
  dotCreate(
    _this.svgNodeMACD,
    auxName + _this.chartName,
    3.5,
    textactive2(colorTheme),
    upColor,
    2,
  );
  dotCreate(
    _this.svgNodeMACD,
    "MACDSignal" + _this.chartName,
    3.5,
    textactive2(colorTheme),
    dnColor,
    2,
  );

  function mouseover() {
    if (_this.transform === undefined || _this.transform == null) {
      _this.transform = zoomTransform(
        select(".hoverParent" + auxName + _this.chartName)._groups[0][0],
      );
    }
    event.preventDefault();
    const xPixels = Math.max(
      Math.min(
        event.layerX - _this.svgNodeMACD.margin.left,
        _this.svgNodeMACD.width,
      ),
      0,
    );
    var xt = _this.transform.rescaleX(_this.svgNodeMACD.x);
    var yt = _this.svgNodeMACD.y;
    let dat = roundDate(xt.invert(xPixels));
    let selData;

    let highlightCandles = true;
    if (dat <= _this.candleData[_this.candleData.length - 1].date) {
      selData = mouseDate(xt.invert(xPixels), _this.candleData);
    } else {
      selData = JSON.parse(
        JSON.stringify(_this.candleData[_this.candleData.length - 1]),
      );
      selData["date"] = dat;
      highlightCandles = false;
    }
    const yPixels = Math.max(
      Math.min(
        event.layerY - _this.svgNodeMACD.margin.top,
        _this.svgNodeMACD.height,
      ),
      0,
    );
    let yRate = yt.invert(yPixels);
    yTooltip
      .style("opacity", 1)
      .style(
        "left",
        _this.svgNodeMACD.width + _this.svgNodeMACD.margin.left + 1 + "px",
      )
      .style("top", yPixels + _this.svgNodeMACD.margin.top - 11 + "px")
      .html(format(_this.currencyFormat)(yRate));

    xTooltip
      .style("opacity", 1)
      .style(
        "left",
        xt(selData.date) +
          _this.svgNodeMACD.margin.left -
          xTooltipWidth / 2 +
          "px",
      )
      .style(
        "top",
        _this.svgNodeMACD.height + _this.svgNodeMACD.margin.top + 2 + "px",
      )
      .html(timeFormat(_this.dateFormat)(selData.date));

    select(".hHoverLineMACD" + _this.chartName)
      .style("opacity", 1)
      .attr("y1", yPixels)
      .attr("y2", yPixels);

    let xPix = xt(selData.date);

    select(".vHoverLineTA" + _this.chartName)
      .style("opacity", 1)
      .attr("x1", xPix)
      .attr("x2", xPix);
    if (_this.showRSI === true) {
      select(".vHoverLineRSI" + _this.chartName)
        .style("opacity", 1)
        .attr("x1", xPix)
        .attr("x2", xPix);
    }
    select(".vHoverLineMACD" + _this.chartName)
      .style("opacity", 1)
      .attr("x1", xPix)
      .attr("x2", xPix);
    if (_this.showSTOCH === true) {
      select(".vHoverLineSTOCH" + _this.chartName)
        .style("opacity", 1)
        .attr("x1", xPix)
        .attr("x2", xPix);
    }

    if (highlightCandles) {
      let bandWidth = _this.transform.k * _this.bandWidth;
      const candleWidth = bandWidth * barWidth + 1;
      const yt2 = _this.svgNode.y;
      if (_this.isCandles) {
        let up = Math.max(selData.open, selData.close);
        let dn = Math.min(selData.open, selData.close);
        select(".cndlStckTA" + _this.chartName)
          .style("opacity", 1)
          .attr("x1", xPix)
          .attr("x2", xPix)
          .attr("y1", yt2(selData.high))
          .attr("y2", yt2(up));
        select(".cndlStck2TA" + _this.chartName)
          .style("opacity", 1)
          .attr("x1", xPix)
          .attr("x2", xPix)
          .attr("y1", yt2(dn))
          .attr("y2", yt2(selData.low));
        select(".cndlTA" + _this.chartName)
          .style("opacity", 1)
          .attr("x", xPix - candleWidth / 2)
          .attr("y", yt2(up))
          .attr("height", Math.max(1, yt2(dn) - yt2(up)))
          .attr("width", candleWidth);
      } else {
        select(".dotTA" + _this.chartName)
          .style("opacity", 1)
          .attr(
            "transform",
            `translate(${xt(selData.date)},${yt2(selData.close)})`,
          );
      }
      if (_this.showRSI === true) {
        const yt3 = _this.svgNodeRSI.y;
        select(".dotRSI" + _this.chartName)
          .style("opacity", 1)
          .attr(
            "transform",
            `translate(${xt(selData.date)},${yt3(selData.rsi)})`,
          );
      }
      select(".dotMACD" + _this.chartName)
        .style("opacity", 1)
        .attr(
          "transform",
          `translate(${xt(selData.date)},${yt(selData.macd)})`,
        );
      select(".dotMACDSignal" + _this.chartName)
        .style("opacity", 1)
        .attr(
          "transform",
          `translate(${xt(selData.date)},${yt(selData.macdSignal)})`,
        );
      select(".barMACDHistogram" + _this.chartName)
        .style("opacity", 1)
        .attr("x", xPix - candleWidth / 2)
        .attr("y", Math.min(yt(0), yt(selData["macdHist"])))
        .attr("height", Math.max(1, Math.abs(yt(0) - yt(selData["macdHist"]))))
        .attr("width", candleWidth);
      if (_this.showSTOCH === true) {
        const yt4 = _this.svgNodeSTOCH.y;
        select(".dotStochPK" + _this.chartName)
          .style("opacity", 1)
          .attr(
            "transform",
            `translate(${xt(selData.date)},${yt4(selData.stochPK)})`,
          );
        select(".dotStochPD" + _this.chartName)
          .style("opacity", 1)
          .attr(
            "transform",
            `translate(${xt(selData.date)},${yt4(selData.stochPD)})`,
          );
      }
    } else {
      if (_this.isCandles) {
        select(".cndlStckTA" + _this.chartName).style("opacity", 0);
        select(".cndlStck2TA" + _this.chartName).style("opacity", 0);
        select(".cndlTA" + _this.chartName).style("opacity", 0);
      } else {
        select(".dotTA" + _this.chartName).style("opacity", 0);
      }
      if (_this.showRSI === true) {
        select(".dotRSI" + _this.chartName).style("opacity", 0);
      }
      select(".barMACDHistogram" + _this.chartName).style("opacity", 0);
      select(".dotMACD" + _this.chartName).style("opacity", 0);
      select(".dotMACDSignal" + _this.chartName).style("opacity", 0);
      if (_this.showSTOCH === true) {
        select(".dotStochPK" + _this.chartName).style("opacity", 0);
        select(".dotStochPD" + _this.chartName).style("opacity", 0);
      }
    }

    displayDataTA(selData, _this);
  }
  function mouseout() {
    yTooltip
      .style("left", "-1000000px")
      .style("top", "0px")
      .style("opacity", 0)
      .html("");
    xTooltip
      .style("left", "-1000000px")
      .style("top", "0px")
      .style("opacity", 0)
      .html("");
    select(".vHoverLineTA" + _this.chartName).style("opacity", 0);
    if (_this.isCandles) {
      select(".cndlStckTA" + _this.chartName).style("opacity", 0);
      select(".cndlStck2TA" + _this.chartName).style("opacity", 0);
      select(".cndlTA" + _this.chartName).style("opacity", 0);
    } else {
      select(".dotTA" + _this.chartName).style("opacity", 0);
    }
    if (_this.showRSI === true) {
      select(".vHoverLineRSI" + _this.chartName).style("opacity", 0);
      select(".dotRSI" + _this.chartName).style("opacity", 0);
    }
    select(".barMACDHistogram" + _this.chartName).style("opacity", 0);
    select(".vHoverLineMACD" + _this.chartName).style("opacity", 0);
    select(".hHoverLineMACD" + _this.chartName).style("opacity", 0);
    select(".dotMACD" + _this.chartName).style("opacity", 0);
    select(".dotMACDSignal" + _this.chartName).style("opacity", 0);
    if (_this.showSTOCH === true) {
      select(".vHoverLineSTOCH" + _this.chartName).style("opacity", 0);
      select(".dotStochPK" + _this.chartName).style("opacity", 0);
      select(".dotStochPD" + _this.chartName).style("opacity", 0);
    }
    displayDataTA(_this.candleData[_this.candleData.length - 1], _this);
  }
  function zoomed() {
    if (!_this.keepDateInterval) {
      setTimeout(() => {
        _this.pickedDateInterval = "-1";
      }, 500);
    }
    yTooltip
      .style("left", "-1000000px")
      .style("top", "0px")
      .style("opacity", 0)
      .html("");
    xTooltip
      .style("left", "-1000000px")
      .style("top", "0px")
      .style("opacity", 0)
      .html("");

    select(".vHoverLineTA" + _this.chartName).style("opacity", 0);
    if (_this.isCandles) {
      select(".cndlStckTA" + _this.chartName).style("opacity", 0);
      select(".cndlStck2TA" + _this.chartName).style("opacity", 0);
      select(".cndlTA" + _this.chartName).style("opacity", 0);
    } else {
      select(".dotTA" + _this.chartName).style("opacity", 0);
    }
    if (_this.showRSI === true) {
      select(".vHoverLineRSI" + _this.chartName).style("opacity", 0);
      select(".dotRSI" + _this.chartName).style("opacity", 0);
    }
    select(".barMACDHistogram" + _this.chartName).style("opacity", 0);
    select(".hHoverLineMACD" + _this.chartName).style("opacity", 0);
    select(".vHoverLineMACD" + _this.chartName).style("opacity", 0);
    select(".dotMACD" + _this.chartName).style("opacity", 0);
    select(".dotMACDSignal" + _this.chartName).style("opacity", 0);
    if (_this.showSTOCH === true) {
      select(".vHoverLineSTOCH" + _this.chartName).style("opacity", 0);
      select(".dotStochPK" + _this.chartName).style("opacity", 0);
      select(".dotStochPD" + _this.chartName).style("opacity", 0);
    }

    _this.transform = event.transform;
    _this.svgNodeMACD.gX.call(
      _this.svgNodeMACD.xAxis.scale(
        _this.transform.rescaleX(_this.svgNodeMACD.x),
      ),
    );
    const xt = _this.transform.rescaleX(_this.svgNodeMACD.x);
    MACDZooming(_this.transform, xt, _this, barWidth);

    // transform other charts
    if (_this.externalZoom === false) {
      _this.externalZoom = true;
      select(".hoverParentTA" + _this.chartName).call(
        _this.zoomTA.transform,
        _this.transform,
      );
      if (_this.showRSI) {
        select(".hoverParentRSI" + _this.chartName).call(
          _this.zoomRSI.transform,
          _this.transform,
        );
      }
      if (_this.showSTOCH) {
        select(".hoverParentSTOCH" + _this.chartName).call(
          _this.zoomSTOCH.transform,
          _this.transform,
        );
      }
      _this.externalZoom = false;
    }
  }
  function zoomEnd() {
    _this.transform = event.transform;
    const xt = _this.transform.rescaleX(_this.svgNodeMACD.x);
    clearTimeout(resizeTimer);
    resizeTimer = setTimeout(function() {
      var xmin = new Date(xt.invert(0));
      var xmax = new Date(xt.invert(_this.svgNodeMACD.width));

      if (!_this.externalZoom) {
        let data = _this.candleData.map((d) => d);
        _this.filteredData = data.filter(function(d) {
          return inRange(d.date, xmin, xmax);
        });
      }

      let minMaxYAxis = _this.getMinMaxMACD(_this.filteredData);
      if (!isFinite(minMaxYAxis[0])) {
        minMaxYAxis[0] = -1;
      }
      if (!isFinite(minMaxYAxis[1])) {
        minMaxYAxis[1] = 1;
      }
      _this.svgNodeMACD.y.domain(minMaxYAxis);
      let yt = _this.svgNodeMACD.y;

      MACDZoomEnd(
        _this.transform,
        xt,
        yt,
        _this.filteredData,
        _this,
        barWidth,
        valueLast,
        lastYTooltip,
      );
    }, 200);
  }

  var resizeTimer;
  zoomSetup(
    _this,
    "svgNode" + auxName,
    "zoom" + auxName,
    auxName,
    _this.chartName,
    mouseover,
    mouseout,
    zoomed,
    zoomEnd,
  );
}

export function MACDZooming(t, xt, _this, barWidth = 0.7) {
  let bandWidth = t.k * _this.bandWidth;
  const candleWidth = bandWidth * barWidth + 1;
  let line, fieldName;
  // macd
  if (_this.showMACD === true) {
    fieldName = "macd";
    line = lineD3()
      .defined((d) => !Number.isNaN(d[fieldName]))
      .x((d) => xt(d.date))
      .y((d) => _this.svgNodeMACD.y(d[fieldName]));
    _this.svgNodeMACD["macd" + _this.chartName].attr("d", (d) => line(d));
    fieldName = "macdSignal";
    line = lineD3()
      .defined((d) => !Number.isNaN(d[fieldName]))
      .x((d) => xt(d.date))
      .y((d) => _this.svgNodeMACD.y(d[fieldName]));
    _this.svgNodeMACD["macdSignal" + _this.chartName].attr("d", (d) => line(d));
    _this.svgNodeMACD["macdHist" + _this.chartName]
      .attr("x", function(d) {
        return xt(d.date) - candleWidth / 2;
      })
      .attr("width", candleWidth);
    _this.svgNodeMACD["zeroLineMACD" + _this.chartName]
      .attr("y1", _this.svgNodeMACD.y(0))
      .attr("y2", _this.svgNodeMACD.y(0));
  }
}

export function MACDZoomEnd(
  t,
  xt,
  yt,
  filtered,
  _this,
  barWidth = 0.7,
  valueLast,
  lastYTooltip,
) {
  let line, fieldName;
  // macd
  if (_this.showMACD === true) {
    fieldName = "macd";
    line = lineD3()
      .defined((d) => !Number.isNaN(d[fieldName]))
      .x((d) => xt(d.date))
      .y((d) => _this.svgNodeMACD.y(d[fieldName]));
    _this.svgNodeMACD["macd" + _this.chartName]
      .data([filtered])
      .join("path")
      .attr("d", (d) => line(d));
    fieldName = "macdSignal";
    line = lineD3()
      .defined((d) => !Number.isNaN(d[fieldName]))
      .x((d) => xt(d.date))
      .y((d) => _this.svgNodeMACD.y(d[fieldName]));
    _this.svgNodeMACD["macdSignal" + _this.chartName]
      .data([filtered])
      .join("path")
      .attr("d", (d) => line(d));

    let bandWidth = t.k * _this.bandWidth;
    const candleWidth = bandWidth * barWidth + 1;
    _this.svgNodeMACD["macdHist" + _this.chartName] = _this.svgNodeMACD[
      "macdHist" + _this.chartName
    ]
      .data(filtered, (d) => d.id)
      .join(
        function(enter) {
          return enter
            .append("rect")
            .attr("x", (d) => xt(d.date) - candleWidth / 2)
            .attr("width", candleWidth)
            .attr("y", (d) => Math.min(yt(0), yt(d["macdHist"])))
            .attr("height", (d) =>
              Math.max(1, Math.abs(yt(0) - yt(d["macdHist"]))),
            )
            .attr("stroke", function(d) {
              return d["colorSel"];
            })
            .style("fill", function(d) {
              return d["fillColorSel"];
            })
            .style("shape-rendering", "crispEdges")
            .style("vector-effect", "non-scaling-stroke");
        },
        function(update) {
          return update
            .attr("x", (d) => xt(d.date) - candleWidth / 2)
            .attr("width", candleWidth)
            .attr("y", (d) => Math.min(yt(0), yt(d["macdHist"])))
            .attr("height", (d) =>
              Math.max(1, Math.abs(yt(0) - yt(d["macdHist"]))),
            )
            .style("shape-rendering", "crispEdges")
            .style("vector-effect", "non-scaling-stroke");
        },
        function(exit) {
          return exit.remove();
        },
      );
    _this.svgNodeMACD["zeroLineMACD" + _this.chartName]
      .attr("y1", _this.svgNodeMACD.y(0))
      .attr("y2", _this.svgNodeMACD.y(0));

    // last rate
    var yMax = yt.invert(0);
    var yMin = yt.invert(_this.svgNodeMACD.height);
    lastYTooltip
      .html(format(_this.currencyFormat)(valueLast))
      .transition()
      .duration(500)
      .style("opacity", yMin <= valueLast && valueLast <= yMax ? 1 : 0)
      .style("top", yt(valueLast) + _this.svgNodeMACD.margin.top - 11 + "px");

    // y axis
    let tickCount = _this.svgNodeMACD.height / 40;
    _this.svgNodeMACD.gY
      .transition()
      .duration(500)
      .call(
        axisRight()
          .tickSize(-_this.svgNodeMACD.width)
          .ticks(tickCount)
          .tickSizeOuter(0)
          .tickPadding(10)
          .scale(_this.svgNodeMACD.y),
      );
  }
}
